package com.evoapps.evoshift.data.mapper

import com.evoapps.evoshift.data.dto.staff.MedicalExamDto
import com.evoapps.evoshift.data.dto.staff.OhsTrainingDto
import com.evoapps.evoshift.data.dto.staff.StaffCheckDto
import com.evoapps.evoshift.data.dto.staff.StaffHrDto
import com.evoapps.evoshift.data.dto.staff.StaffReadinessDto
import com.evoapps.evoshift.domain.model.CheckStatus
import com.evoapps.evoshift.domain.model.StaffCheck
import com.evoapps.evoshift.domain.model.StaffCheckRecord
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.model.StaffRecord
import kotlinx.datetime.LocalDate

/** SH-47 staff documents. Unreadable dates read as missing, unknown statuses as MISSING. */
object StaffMapper {
    private fun date(value: String?): LocalDate? = value?.let { runCatching { LocalDate.parse(it.take(10)) }.getOrNull() }

    fun toDomain(dto: StaffHrDto, userId: String, workplaceId: String) = StaffRecord(
        userId = userId,
        workplaceId = workplaceId,
        ohsTrainingDate = date(dto.ohsTraining?.completedOn),
        ohsValidUntil = date(dto.ohsTraining?.validUntil),
        medicalExamValidUntil = date(dto.medicalExam?.validUntil),
        updatedAt = dto.updatedAt,
        updatedBy = dto.updatedBy,
        medicalExamDate = date(dto.medicalExam?.examinedOn),
        history = dto.history.mapNotNull { h ->
            val check = StaffCheck.entries.firstOrNull { it.name == h.check } ?: return@mapNotNull null
            StaffCheckRecord(check, date(h.from), date(h.validUntil)).takeIf { it.from != null || it.validUntil != null }
        }
    )

    fun toDto(record: StaffRecord) = StaffHrDto(
        userId = record.userId,
        workplaceId = record.workplaceId,
        medicalExam = if (record.medicalExamValidUntil == null && record.medicalExamDate == null) {
            null
        } else {
            MedicalExamDto(validUntil = record.medicalExamValidUntil?.toString(), examinedOn = record.medicalExamDate?.toString())
        },
        history = record.history.take(StaffRecord.MAX_HISTORY).map { StaffCheckDto(it.check.name, it.from?.toString(), it.validUntil?.toString()) },
        ohsTraining = if (record.ohsTrainingDate == null && record.ohsValidUntil == null) {
            null
        } else {
            OhsTrainingDto(completedOn = record.ohsTrainingDate?.toString(), validUntil = record.ohsValidUntil?.toString())
        },
        updatedAt = record.updatedAt,
        updatedBy = record.updatedBy
    )

    private fun status(name: String) = CheckStatus.entries.firstOrNull { it.name == name } ?: CheckStatus.MISSING

    fun toDomain(dto: StaffReadinessDto, userId: String, workplaceId: String) = StaffReadiness(
        userId = userId,
        workplaceId = workplaceId,
        medical = status(dto.medical),
        ohs = status(dto.ohs),
        checkedOn = date(dto.checkedOn)
    )

    fun toDto(readiness: StaffReadiness) = StaffReadinessDto(
        userId = readiness.userId,
        workplaceId = readiness.workplaceId,
        medical = readiness.medical.name,
        ohs = readiness.ohs.name,
        checkedOn = readiness.checkedOn?.toString()
    )
}
